import { Box, Flex, Spinner, Text } from "@chakra-ui/react"
import { useEffect, useState } from "react"
import { getMonthlyReportPivotRelationship } from "../../../Http/Index"
import { PivotTable } from "./PivotTable"

interface Props {
  selectedMonth: string
  selectedQuarter?: string
  selectedCompanyId?: string
}

const ASSISTANCE_TYPES = [
  "End to End support",
  "End to End w/o Dr Consult",
  "Emergency doctor Consult",
  "Insurance Support",
  "Cancelled / Other",
]

export const Relationship: React.FC<Props> = ({
  selectedMonth,
  selectedQuarter = "",
  selectedCompanyId = "",
}) => {
  const [data, setData] = useState<any>(null)
  const [quarters, setQuarters] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedQuarter, selectedCompanyId])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const params: Record<string, any> = {}
      if (selectedQuarter) params.quarter = selectedQuarter
      if (selectedCompanyId) params.companyId = selectedCompanyId
      const res = await getMonthlyReportPivotRelationship(selectedMonth, params)
      const d = res.data?.data || {}

      // derive quarters from raisedByForWhom for the 3rd table columns
      const qs: string[] = (d.raisedByForWhom || [])
        .flatMap((r: any) => (r.raisedFor || []).map((f: any) => f.relation))
        .filter((v: string, i: number, a: string[]) => v && a.indexOf(v) === i)
      if (qs.length) setQuarters(qs)

      setData(d)
    } catch {
    } finally {
      setIsLoading(false)
    }
  }

  // Table 1 — Raised By × Assistance
  const raisedByRows = (data?.raisedByAssistance || []).map((item: any) => {
    const values = Object.fromEntries((item.assistances || []).map((a: any) => [a.type, a.count]))
    const total = ASSISTANCE_TYPES.reduce((sum, col) => sum + (values[col] || 0), 0)
    return { rowLabel: item._id, values, total }
  })

  // Table 2 — Raised For × Assistance
  const raisedForRows = (data?.raisedForAssistance || []).map((item: any) => {
    const values = Object.fromEntries((item.assistances || []).map((a: any) => [a.type, a.count]))
    const total = ASSISTANCE_TYPES.reduce((sum, col) => sum + (values[col] || 0), 0)
    return { rowLabel: item._id, values, total }
  })

  // Table 3 — Raised By × For Whom
  const raisedByForWhomRows = (data?.raisedByForWhom || []).map((item: any) => ({
    rowLabel: item._id,
    values: Object.fromEntries((item.raisedFor || []).map((f: any) => [f.relation, f.count])),
    total: item.total,
  }))

  return (
    <Box mt={2}>

      {isLoading ? (
        <Flex justify="center" py={10}><Spinner /></Flex>
      ) : !data ? (
        <Text color="gray.400" textAlign="center" py={6}>No data</Text>
      ) : (
        <Flex direction="column" gap={8}>
          <PivotTable
            title="Ticket Raised By and Assistance Provided"
            rows={raisedByRows}
            columns={ASSISTANCE_TYPES}
          />
          <PivotTable
            title="Ticket Raised For and Assistance Provided"
            rows={raisedForRows}
            columns={ASSISTANCE_TYPES}
          />
          <PivotTable
            title="Ticket Raised By and For Whom"
            rows={raisedByForWhomRows}
            columns={quarters}
          />
        </Flex>
      )}
    </Box>
  )
}
